import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './useAuth';
import { appApi } from '../utils/api';

interface AppRightsContextValue {
  /** 当前账号是否拥有支付应用（Application）权限 */
  canCreate: boolean;
  /** 权限是否已加载完成 */
  rightsLoaded: boolean;
}

const AppRightsContext = createContext<AppRightsContextValue>({
  canCreate: false,
  rightsLoaded: false,
});

/**
 * 在登录态下拉取一次 /app/rights，供侧边栏菜单与页面共享。
 * 挂在 AdminRoutes 层，页面切换不会重复请求。
 */
export const AppRightsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [canCreate, setCanCreate] = useState(false);
  const [rightsLoaded, setRightsLoaded] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      // 未登录/退出时复位
      setCanCreate(false);
      setRightsLoaded(false);
      return;
    }

    let cancelled = false;
    setRightsLoaded(false);

    (async () => {
      try {
        const res = await appApi.getRights();
        if (!cancelled) {
          setCanCreate(res.code === 1 && !!res.data?.can_create);
        }
      } catch (err) {
        console.error('Failed to fetch app rights:', err);
        if (!cancelled) setCanCreate(false);
      }
      if (!cancelled) setRightsLoaded(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  return (
    <AppRightsContext.Provider value={{ canCreate, rightsLoaded }}>
      {children}
    </AppRightsContext.Provider>
  );
};

export const useAppRights = (): AppRightsContextValue => {
  return useContext(AppRightsContext);
};
